import React from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { channelName } from './reach';
import { RollNumber } from '../common/RollNumber';
import { CampaignResults as Results, compact, pct, REACH_CAMPAIGN_RESULTS } from './reachVisits';

/**
 * A campaign's results (App Prototype → Reach → Campaigns → Summer
 * checklist): clicks to the site, sign-ups and the click → sign-up rate, then
 * by channel and by post, so a marketer can see what to repeat. Clicks come
 * from the site's Google Analytics through each post's tagged link; sign-ups
 * from the site's own forms, sent by visitors who came from a post
 * (smagent-apiserver reach/services/siteVisits.js). An always-on campaign
 * shows its last 30 days. Impressions are the views the networks report for
 * the campaign's posts (engine post.metrics); "—" where a network reports none.
 */

const n = (v: number) => v.toLocaleString();

const postDay = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' }) : '';

export function CampaignResults({ campaignId }: { campaignId: string }) {
  const { data } = useQuery<{ reachCampaignResults: Results }>(REACH_CAMPAIGN_RESULTS, {
    variables: { campaignId },
    fetchPolicy: 'cache-and-network',
  });
  const r = data?.reachCampaignResults;
  if (!r) return null;
  const head = r.window ? `Results · last ${r.window} days` : 'Results';
  if (!r.posts) {
    return (
      <section className="rcp-results" aria-label="Results">
        <h3 className="rcp-h3">{head}</h3>
        <p className="ink-muted">Results show here once a post goes out.</p>
      </section>
    );
  }
  // Clicks need the site's Google Analytics; sign-ups don't.
  const clicks = r.connected && r.syncedAt;
  return (
    <section className="rcp-results" aria-label="Results">
      <h3 className="rcp-h3">{head}</h3>
      <div className="rcp-res-tiles">
        {r.impressions != null ? (
          <div>
            <b>
              <RollNumber value={compact(r.impressions)} />
            </b>
            <span>Impressions</span>
          </div>
        ) : null}
        <div>
          <b>{clicks ? <RollNumber value={r.clicks} /> : '—'}</b>
          <span>Clicks to your site</span>
        </div>
        <div>
          <b>
            <RollNumber value={r.signups} />
          </b>
          <span>Sign-ups</span>
        </div>
        <div>
          <b>{clicks ? <RollNumber value={pct(r.conversion)} /> : '—'}</b>
          <span>Click → sign-up</span>
        </div>
        <div>
          <b>
            <RollNumber value={r.posts} />
          </b>
          <span>{r.posts === 1 ? 'Post' : 'Posts'}</span>
        </div>
      </div>
      {!r.connected ? (
        <p className="rcp-res-note">
          <Link to="/content?view=analytics">Connect Google Analytics</Link> to count the clicks
          each post sends to your site.
        </p>
      ) : null}

      {r.byChannel.length > 1 ? (
        <>
          <div className="rcp-res-head">By channel</div>
          <div className="rcp-res-table" role="table">
            <div className="rcp-res-row rcp-res-cols" role="row">
              <span>Channel</span>
              <span>Posts</span>
              <span>Impressions</span>
              <span>Clicks</span>
              <span>Sign-ups</span>
              <span>Conversion</span>
            </div>
            {r.byChannel.map((c) => (
              <div className="rcp-res-row" role="row" key={c.socialProfileId}>
                <span>{channelName(c.platform)}</span>
                <span>{n(c.posts)}</span>
                <span>{c.views != null ? compact(c.views) : '—'}</span>
                <span>{clicks ? n(c.visits) : '—'}</span>
                <span>{n(c.signups)}</span>
                <span>{clicks ? pct(c.conversion) : '—'}</span>
              </div>
            ))}
          </div>
        </>
      ) : null}

      {r.byPost.length ? (
        <>
          <div className="rcp-res-head">
            By post
            <span className="ink-muted">
              {r.posts > r.byPost.length
                ? ` · top ${r.byPost.length} of ${r.posts}, most sign-ups first`
                : ' · most sign-ups first'}
            </span>
          </div>
          <div className="rcp-res-table" role="table">
            <div className="rcp-res-row rcp-res-cols rcp-res-post" role="row">
              <span>Post</span>
              <span>Impr.</span>
              <span>Clicks</span>
              <span>Sign-ups</span>
              <span>Conv.</span>
            </div>
            {r.byPost.map((p) => (
              <div className="rcp-res-row rcp-res-post" role="row" key={p.postId}>
                <span className="rcp-res-text">
                  <span>{p.text || 'A post with no text'}</span>
                  <span className="ink-muted">
                    {channelName(p.platform)}
                    {p.at ? ` · ${postDay(p.at)}` : ''}
                  </span>
                </span>
                <span>{p.views != null ? compact(p.views) : '—'}</span>
                <span>{clicks ? n(p.visits) : '—'}</span>
                <span>{n(p.signups)}</span>
                <span>{clicks ? pct(p.conversion) : '—'}</span>
              </div>
            ))}
          </div>
        </>
      ) : clicks ? (
        <p className="rcp-res-note">No clicks or sign-ups from these posts yet.</p>
      ) : null}
    </section>
  );
}
